/*
 * A lab that cannot measure below (or above) its assay limit prints the limit
 * instead of a number: "<2", "<0.05", ">2250". The app stores markers as
 * numbers, so a limit left as typed is text — it charts nothing, grades
 * nothing, and drags no pattern. Each one is therefore substituted for a number
 * just inside the limit, which is what a practitioner would enter by hand.
 *
 * The table below is a set of DELIBERATE, per-marker decisions, not arithmetic.
 * Most step one tick under the limit, but how big a tick is depends on the
 * marker: tgAb "<15" is 14.99 where estradiolE2 "<15" is 14.9.
 *
 * A limit the table does not cover used to be left alone, and that was a bug
 * rather than caution: the value stayed text, and a test carrying text in a
 * numeric marker CANNOT BE SAVED AT ALL. So there is now a general rule behind
 * the table — see fallbackAssayLimitSubstitution — and the table's job is to
 * override it wherever a marker needs a different answer than arithmetic gives.
 *
 * A substitution has to land inside the range this app grades the marker
 * against, not just under the limit the lab printed. tpoAb is the case in
 * point, and the reason "<9" is 8 rather than 8.99: the tpoAb lab max here is
 * 8.9, so anything nearer the limit would flag an UNDETECTABLE antibody as out
 * of range. Leave it at 8 until the tpoAb ranges themselves are revisited.
 *
 * Matching is numeric, on purpose. These used to be string comparisons against
 * the exact spelling seen on one report, so the same limit printed with another
 * decimal place ("<2.00" beside "<2.0") silently fell through and stayed text.
 * parseAssayLimit reads the direction and the number, which makes "<2", "<2.0",
 * "<2.00", "≤2" and "< 2" one entry.
 */
type AssayLimit = { direction: '<' | '>'; limit: number };

export const parseAssayLimit = (raw: any): AssayLimit | undefined => {
  if (typeof raw !== 'string') return undefined;
  const match = /^([<>≤≥])=?\s*(\d+\.?\d*|\.\d+)$/.exec(raw.trim());
  if (!match) return undefined;
  const limit = Number(match[2]);
  if (!isFinite(limit)) return undefined;
  return { direction: match[1] === '<' || match[1] === '≤' ? '<' : '>', limit };
};

const ASSAY_LIMIT_SUBSTITUTIONS: {
  [fieldId: string]: Array<{ direction: '<' | '>'; limit: number; value: number }>;
} = {
  tpoAb: [
    { direction: '<', limit: 0.3, value: 0.2 },
    { direction: '<', limit: 1, value: 0.9 },
    { direction: '<', limit: 9, value: 8 },
    { direction: '<', limit: 12, value: 11.9 },
    { direction: '<', limit: 28, value: 27.9 },
  ],
  tgAb: [
    { direction: '<', limit: 1, value: 0.9 },
    { direction: '<', limit: 1.5, value: 1.4 },
    { direction: '<', limit: 2, value: 1.9 },
    { direction: '<', limit: 15, value: 14.99 },
    { direction: '>', limit: 2250, value: 2251 },
  ],
  estroneE1: [{ direction: '<', limit: 6, value: 5.9 }],
  estriolE3: [
    { direction: '<', limit: 0.1, value: 0.09 },
    { direction: '<', limit: 0.2, value: 0.19 },
  ],
  estradiolE2: [
    { direction: '<', limit: 5, value: 4.9 },
    { direction: '<', limit: 10, value: 9.9 },
    { direction: '<', limit: 15, value: 14.9 },
    { direction: '<', limit: 30, value: 29.9 },
  ],
  bilirubin: [{ direction: '<', limit: 0.2, value: 0.19 }],
  bilirubinDirect: [{ direction: '<', limit: 0.2, value: 0.19 }],
  insulin: [{ direction: '<', limit: 3, value: 2.9 }],
  testosteroneFreeDirect: [{ direction: '<', limit: 0.2, value: 0.19 }],
  testosteroneTotal: [{ direction: '<', limit: 2.5, value: 2.4 }],
  lipoproteinA: [
    { direction: '<', limit: 7, value: 6.9 },
    { direction: '<', limit: 8.4, value: 8.3 },
    { direction: '<', limit: 10, value: 9.9 },
    { direction: '<', limit: 19.6, value: 19.5 },
  ],
  folateSerum: [
    { direction: '>', limit: 20, value: 21 },
    { direction: '>', limit: 24, value: 25 },
  ],
  folateRBC: [
    { direction: '>', limit: 1325, value: 1326 },
    { direction: '>', limit: 1396, value: 1397 },
  ],
  bileAcids: [{ direction: '<', limit: 1, value: 0.9 }],
  ldl: [{ direction: '<', limit: 100, value: 99 }],
  pregnenolone: [{ direction: '<', limit: 5, value: 4.99 }],
  progesterone: [
    { direction: '<', limit: 0.05, value: 0.04 },
    { direction: '<', limit: 0.1, value: 0.09 },
    { direction: '<', limit: 0.5, value: 0.4 },
  ],
  lh: [{ direction: '<', limit: 0.05, value: 0.04 }],
  hsCRP: [
    { direction: '<', limit: 0.15, value: 0.14 },
    { direction: '<', limit: 0.2, value: 0.19 },
  ],
  dDimer: [{ direction: '<', limit: 0.2, value: 0.19 }],
  psaTotal: [{ direction: '<', limit: 0.01, value: 0.009 }],
  psaFree: [{ direction: '<', limit: 0.01, value: 0.009 }],
  /*
   * NMR LipoProfile. Both of these are curated for the reason the doc comment
   * above gives: the fallback steps one decimal place finer than the limit,
   * which on an INTEGER-precision marker lands at n - 0.1 and then displays
   * rounded straight back to n. A practitioner reading "LP-IR 25" against a
   * report that says "<25" has been shown the limit as if it were the result.
   *
   * n - 1 instead, which is the app's documented rule for a printed "<n"
   * (LabPanelCatalog.ts) and is a real value at this precision. Both limits
   * sit well inside the good end of their ladders (LP-IR low is <27, small
   * LDL-P low is <117), so neither substitution changes a grade.
   */
  lpIrScore: [{ direction: '<', limit: 25, value: 24 }],
  smallLdlParticleNumber: [{ direction: '<', limit: 90, value: 89 }],
};

/** How many decimal places a number is carrying, read off the NUMBER. */
const decimalPlaces = (value: number): number => {
  const text = String(value);
  // Exponent notation ("1e-7") has no decimal point to count. No assay limit on
  // any report is that small, and guessing at one is worse than declining.
  if (text.includes('e') || text.includes('E')) return NaN;
  const point = text.indexOf('.');
  return point === -1 ? 0 : text.length - point - 1;
};

/**
 * One tick inside a limit nobody has curated, so the test can still be saved.
 *
 * THE RULE: step by one unit at one decimal place FINER than the limit's own
 * precision. `<1` becomes 0.9, `<0.1` becomes 0.09, `<0.01` becomes 0.009, and
 * `>20` becomes 20.1.
 *
 * Precision is taken from the NUMBER, not from how the lab spelled it, so
 * "<1.0", "<1.00" and "<1" all give 0.9 rather than three different answers.
 *
 * Going one place finer rather than a whole tick at the printed precision is
 * what keeps the result meaningful at the bottom of a scale: a whole tick under
 * `<0.1` is zero, which is not "just under the limit", it is a different claim.
 * This rule never reaches zero, never goes negative, and never changes the order
 * of magnitude.
 *
 * Checked against every curated entry above: it reproduces 14 of 25 exactly,
 * including the whole x.9 family and 0.19 / 0.09 / 0.009. It cannot reproduce
 * all of them because that table is a set of per-marker judgements rather than
 * arithmetic — `<0.2` is 0.19 for six markers while `<0.3` is 0.2 for one — and
 * where the two disagree THE TABLE WINS.
 *
 * KNOWN LIMITATION, and the reason a curated entry is still worth adding: this
 * cannot see the range the marker is graded against. tpoAb is the standing
 * example — `<9` is curated to 8, not 8.9, because the tpoAb lab max is 8.9 and
 * anything nearer the limit would flag an UNDETECTABLE antibody as out of range.
 * A marker that starts grading oddly after an uncurated limit lands on it wants
 * an entry in the table, which is exactly how the table grew.
 */
export const fallbackAssayLimitSubstitution = (
  raw: any,
): number | undefined => {
  const parsed = parseAssayLimit(raw);
  if (!parsed) return undefined;

  const places = decimalPlaces(parsed.limit);
  if (!isFinite(places)) return undefined;

  const finer = places + 1;
  const tick = Math.pow(10, -finer);
  const stepped =
    parsed.direction === '<' ? parsed.limit - tick : parsed.limit + tick;

  // Rounded at the precision the tick is expressed in, because 0.2 - 0.01 is
  // 0.19000000000000003 in binary floating point and that is what would be
  // stored, charted and printed.
  const value = Number(stepped.toFixed(finer));

  // A limit of 0 or less is not a measurement this can step inside of.
  return parsed.direction === '<' && value <= 0 ? undefined : value;
};

/**
 * The number to store for an assay limit a lab printed, or undefined when the
 * value is not a limit at all.
 *
 * The curated table first, because those are deliberate per-marker decisions and
 * several of them exist to keep a value inside the range the marker is graded
 * against. Anything it does not cover falls through to the general rule rather
 * than being left as text: a limit left as typed cannot be stored as a number,
 * so the test it is on CANNOT BE SAVED. That was the whole bug — the app would
 * refuse a test carrying `<0.2` for any marker not on the list.
 */
export const assayLimitSubstitution = (
  fieldId: string,
  raw: any,
): number | undefined => {
  const parsed = parseAssayLimit(raw);
  if (!parsed) return undefined;

  const curated = ASSAY_LIMIT_SUBSTITUTIONS[fieldId]?.find(
    entry =>
      entry.direction === parsed.direction && entry.limit === parsed.limit,
  )?.value;

  return curated !== undefined ? curated : fallbackAssayLimitSubstitution(raw);
};
